// Project 360 — landing page for a single project at /projects/$id.
//
// Adapted from the "Project 360.html" design-handoff bundle. Sections that
// require data not yet served by the backend (Gantt with dependencies, live
// Azure DevOps work items, budget-by-pricing-type, time-vs-estimate burndown,
// sponsors, linked systems) are omitted rather than mocked — they can be
// layered in once the backing endpoints exist.

import { useMemo, useState } from 'react'
import { useQuery, useQueries, useQueryClient } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import {
  ArrowLeft, Pencil, Clock, Calendar, User as UserIcon,
  Folder, Layers, Users, ExternalLink,
} from 'lucide-react'
import { api } from '@/lib/api/client'
import type {
  Project, Phase, ProjectHoursSummary,
  PhaseResource, Activity, FortnoxCustomer,
} from '@/types/api'
import RatesTable from '@/components/rates/RatesTable'
import { ProjectDrawer } from './ProjectDrawer'
import './projectOverview.css'

// ─── Helpers ──────────────────────────────────────────────────────────────────

function fmt(n: number): string {
  return n % 1 === 0 ? String(n) : n.toFixed(1)
}

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0) return '?'
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
}

// Stable hue from a name, for avatar gradients.
function hueFor(name: string): number {
  let h = 0
  for (const c of name) h = (h * 31 + c.charCodeAt(0)) >>> 0
  return h % 360
}

function avatarStyle(name: string): React.CSSProperties {
  const h = hueFor(name)
  return {
    background: `linear-gradient(135deg, oklch(0.6 0.14 ${h}), oklch(0.5 0.16 ${(h + 60) % 360}))`,
  }
}

function fmtDate(s: string | null | undefined): string {
  if (!s) return '—'
  const d = new Date(s + 'T00:00:00')
  if (Number.isNaN(d.getTime())) return s
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
}

function pctOf(num: number, den: number): number {
  return den > 0 ? Math.round((num / den) * 100) : 0
}

// ─── KPIs ─────────────────────────────────────────────────────────────────────

interface KpisProps {
  hours:       ProjectHoursSummary | undefined
  phases:      Phase[]
  totalBudget: number  // sum of phase-resource budget hours, in h
  startDate:   string | null
  endDate:     string | null
}

function Kpis({ hours, phases, totalBudget, startDate, endDate }: KpisProps) {
  const logged    = hours?.totalWorkedHrs   ?? 0
  const billable  = hours?.totalBillableHrs ?? 0
  const entries   = hours?.entryCount       ?? 0
  const loggedPct = pctOf(logged, totalBudget)
  const billPct   = pctOf(billable, logged)

  // Schedule: % of calendar elapsed between earliest start and latest end.
  let schedulePct = 0
  let scheduleLabel = '—'
  let scheduleTone: 'good' | 'warn' | 'danger' | '' = ''
  if (startDate && endDate) {
    const s = new Date(startDate + 'T00:00:00').getTime()
    const e = new Date(endDate   + 'T00:00:00').getTime()
    const now = Date.now()
    if (e > s) {
      schedulePct = Math.max(0, Math.min(100, Math.round(((now - s) / (e - s)) * 100)))
      // ahead / on-track / behind heuristic vs. logged %
      const diff = loggedPct - schedulePct
      if (diff > 10)        { scheduleTone = 'danger'; scheduleLabel = 'Over pace' }
      else if (diff < -10)  { scheduleTone = 'warn';   scheduleLabel = 'Behind' }
      else                  { scheduleTone = 'good';   scheduleLabel = 'On track' }
    }
  }

  const activePhases = phases.filter(p => p.isActive && p.status === 'active').length

  return (
    <section className="p360-kpis">
      <div className="p360-kpi">
        <div className="p360-kpi-label">Time logged</div>
        <div className="p360-kpi-val">
          {fmt(logged)}
          {totalBudget > 0 && <span className="unit">/ {fmt(totalBudget)}h</span>}
        </div>
        {totalBudget > 0 && (
          <div className="p360-kpi-bar"><div style={{ width: `${Math.min(100, loggedPct)}%` }} /></div>
        )}
        <div className="p360-kpi-sub">
          {totalBudget > 0 ? `${loggedPct}% of estimated` : 'No estimate set'}
        </div>
      </div>

      <div className="p360-kpi">
        <div className="p360-kpi-label">Schedule</div>
        <div className={`p360-kpi-val ${scheduleTone}`}>{scheduleLabel}</div>
        {startDate && endDate && (
          <div className={`p360-kpi-bar ${scheduleTone}`}>
            <div style={{ width: `${schedulePct}%` }} />
          </div>
        )}
        <div className={`p360-kpi-sub ${scheduleTone}`}>
          {startDate && endDate
            ? `${schedulePct}% time elapsed · ${loggedPct}% logged`
            : 'No dates set on phases'}
        </div>
      </div>

      <div className="p360-kpi">
        <div className="p360-kpi-label">Billable share</div>
        <div className="p360-kpi-val">
          {billPct}<span className="unit">%</span>
        </div>
        <div className="p360-kpi-bar"><div style={{ width: `${billPct}%` }} /></div>
        <div className="p360-kpi-sub">{fmt(billable)}h billable of {fmt(logged)}h</div>
      </div>

      <div className="p360-kpi">
        <div className="p360-kpi-label">Activity</div>
        <div className="p360-kpi-val">{entries}</div>
        <div className="p360-kpi-sub">
          {entries === 1 ? 'time entry' : 'time entries'} · {activePhases} active {activePhases === 1 ? 'phase' : 'phases'}
        </div>
      </div>
    </section>
  )
}

// ─── Properties ───────────────────────────────────────────────────────────────

function PropertiesCard({ project, startDate, endDate, totalBudget, onEdit }: {
  project:     Project
  startDate:   string | null
  endDate:     string | null
  totalBudget: number
  onEdit:      () => void
}) {
  return (
    <div className="card">
      <div className="p360-card-head">
        <div>
          <div className="p360-card-title">Properties</div>
          <div className="p360-card-sub">Click "Edit all" to open the project drawer</div>
        </div>
        <button className="btn btn-ghost btn-sm" style={{ gap: 6 }} onClick={onEdit}>
          <Pencil size={11} />Edit all
        </button>
      </div>
      <div className="p360-card-body">
        <div className="p360-props">
          <div className="p360-prop">
            <div className="l">Customer</div>
            <div className="v">
              {project.customerName ?? <span className="muted" style={{ fontStyle: 'italic' }}>Internal</span>}
            </div>
          </div>
          <div className="p360-prop">
            <div className="l">Status</div>
            <div className="v">
              <span className={`p360-status ${project.status}`}>{project.status.replace('_', ' ')}</span>
            </div>
          </div>
          {project.fortnoxProjectNumber && (
            <div className="p360-prop">
              <div className="l">Fortnox #</div>
              <div className="v mono">{project.fortnoxProjectNumber}</div>
            </div>
          )}
          {project.invoiceDetailLevelOverride && (
            <div className="p360-prop">
              <div className="l">Invoice detail</div>
              <div className="v">{project.invoiceDetailLevelOverride}</div>
            </div>
          )}
          <div className="p360-prop">
            <div className="l">Start date</div>
            <div className="v">{fmtDate(startDate)}</div>
          </div>
          <div className="p360-prop">
            <div className="l">End date</div>
            <div className="v">{fmtDate(endDate)}</div>
          </div>
          <div className="p360-prop">
            <div className="l">Total estimate</div>
            <div className="v">{totalBudget > 0 ? `${fmt(totalBudget)}h` : '—'}</div>
          </div>
          <div className="p360-prop">
            <div className="l">Project manager</div>
            <div className="v">{project.projectManagerName ?? '—'}</div>
          </div>
          <div className="p360-prop">
            <div className="l">Created</div>
            <div className="v mono">{project.createdAt?.slice(0, 10) ?? '—'}</div>
          </div>
        </div>
        {project.description && (
          <div style={{ marginTop: 16, paddingTop: 14, borderTop: '1px solid var(--ns-border)' }}>
            <div style={{
              fontSize: 11, fontWeight: 600, textTransform: 'uppercase',
              letterSpacing: '0.04em', color: 'var(--ns-text-subtle)', marginBottom: 6,
            }}>Description</div>
            <p style={{ fontSize: 13, lineHeight: 1.55, margin: 0 }}>{project.description}</p>
          </div>
        )}
      </div>
    </div>
  )
}

// ─── PM card ──────────────────────────────────────────────────────────────────

function ManagerCard({ project }: { project: Project }) {
  const pmName = project.projectManagerName
  return (
    <div className="card">
      <div className="p360-card-head">
        <div className="p360-card-title">Project manager</div>
      </div>
      <div className="p360-card-body">
        {pmName ? (
          <div className="p360-pm">
            <div className="p360-avatar" style={avatarStyle(pmName)}>{initials(pmName)}</div>
            <div style={{ flex: 1 }}>
              <div className="p360-pm-name">{pmName}</div>
              <div className="p360-pm-role">Project manager · Internal</div>
            </div>
          </div>
        ) : (
          <div className="muted" style={{ fontSize: 12.5, paddingBottom: 12 }}>
            No project manager assigned.
          </div>
        )}

        {project.customerName && (
          <>
            <div style={{
              fontSize: 11, color: 'var(--ns-text-subtle)', textTransform: 'uppercase',
              letterSpacing: '0.04em', fontWeight: 600, marginBottom: 10,
            }}>Customer</div>
            <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 6 }}>
              <div className="p360-avatar" style={{ ...avatarStyle(project.customerName), width: 36, height: 36, fontSize: 12 }}>
                {initials(project.customerName)}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 500 }}>{project.customerName}</div>
                {project.fortnoxProjectNumber && (
                  <div style={{ fontSize: 12, color: 'var(--ns-text-subtle)' }}>
                    Fortnox <span className="mono">#{project.fortnoxProjectNumber}</span>
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  )
}

// ─── Project structure tree ──────────────────────────────────────────────────

interface PhaseDetail {
  phase:      Phase
  activities: Activity[]
  resources:  PhaseResource[]
  budgetHrs:  number
  loggedHrs:  number
}

function PhaseAvatars({ resources }: { resources: PhaseResource[] }) {
  const visible = resources.slice(0, 5)
  const extra = resources.length - visible.length
  return (
    <span className="p360-stack">
      {visible.map(r => (
        <span key={r.id} className="av" style={avatarStyle(r.personName)} title={r.personName}>
          {initials(r.personName)}
        </span>
      ))}
      {extra > 0 && <span className="av more">+{extra}</span>}
    </span>
  )
}

function ProgressCell({
  loggedHrs, budgetHrs, dateLabel, schedulePct,
}: {
  loggedHrs:   number
  budgetHrs:   number
  dateLabel?:  string
  schedulePct?: number
}) {
  const pct = pctOf(loggedHrs, budgetHrs)
  const tone =
    budgetHrs === 0 ? '' :
    pct >= 100 ? 'over' :
    schedulePct !== undefined && pct < schedulePct - 15 ? 'warn' :
    schedulePct !== undefined && pct >= schedulePct - 15 && pct <= schedulePct + 15 ? 'good' :
    ''

  return (
    <div className="p360-progress">
      <div className={`p360-pbar ${tone}`}>
        <div className="fill" style={{ width: `${Math.min(100, pct)}%` }} />
        {schedulePct !== undefined && schedulePct > 0 && schedulePct < 100 && (
          <span
            style={{
              position: 'absolute', top: -3, width: 2, height: 12,
              background: 'var(--ns-text)', borderRadius: 1,
              left: `${schedulePct}%`,
            }}
            title="Today"
          />
        )}
      </div>
      <div className="p360-pmeta">
        <span className={`l ${tone === 'over' ? 'over' : ''}`}>
          {fmt(loggedHrs)}h{budgetHrs > 0 && <> / {fmt(budgetHrs)}h</>}
        </span>
        <span>{dateLabel ?? (budgetHrs > 0 ? `${pct}%` : '—')}</span>
      </div>
    </div>
  )
}

function StructureCard({
  phaseDetails, totalBudget, totalLogged,
}: {
  phaseDetails: PhaseDetail[]
  totalBudget:  number
  totalLogged:  number
}) {
  return (
    <section className="card" style={{ marginBottom: 16 }}>
      <div className="p360-card-head">
        <div>
          <div className="p360-card-title">Project structure</div>
          <div className="p360-card-sub">Phases, activities, resources and budgets</div>
        </div>
      </div>

      <div className="p360-struct">
        <div className="p360-struct-row p360-struct-head">
          <div>Phase / Activity</div>
          <div>Type</div>
          <div>Estimate</div>
          <div className="r">Resources</div>
          <div className="r">Progress</div>
        </div>

        {phaseDetails.map(({ phase, activities, resources, budgetHrs, loggedHrs }, idx) => {
          const phaseSchedule = phaseSchedulePct(phase)
          const dateLabel =
            phase.startDate && phase.endDate
              ? `${fmtDate(phase.startDate).replace(/ \d{4}/, '')} – ${fmtDate(phase.endDate).replace(/ \d{4}/, '')}`
              : undefined

          return (
            <div key={phase.id}>
              <div className={`p360-struct-row phase ${phase.status === 'closed' ? 'closed' : ''}`}>
                <div>
                  <span className="phase-icon">{idx + 1}</span>
                  <span>{phase.name}</span>
                  <span className={`p360-status ${phase.status === 'closed' ? 'archived' : 'active'}`} style={{ marginLeft: 6 }}>
                    {phase.status === 'closed' ? 'Closed' : 'Active'}
                  </span>
                </div>
                <div>{phase.deliveryType ?? <span className="muted">—</span>}</div>
                <div>{budgetHrs > 0 ? `${fmt(budgetHrs)}h` : '—'}</div>
                <div className="r"><PhaseAvatars resources={resources} /></div>
                <div className="r">
                  <ProgressCell
                    loggedHrs={loggedHrs}
                    budgetHrs={budgetHrs}
                    dateLabel={dateLabel}
                    schedulePct={phaseSchedule}
                  />
                </div>
              </div>

              {activities.filter(a => a.isActive).map(act => (
                <div key={act.id} className="p360-struct-row activity">
                  <div><span className="act-dot" />{act.name}</div>
                  <div><span className="muted">—</span></div>
                  <div><span className="muted">—</span></div>
                  <div className="r"><PhaseAvatars resources={resources} /></div>
                  <div className="r"><span className="muted" style={{ fontSize: 11 }}>see phase</span></div>
                </div>
              ))}
            </div>
          )
        })}

        {phaseDetails.length === 0 && (
          <div style={{ padding: 24, textAlign: 'center' }} className="muted">
            No phases yet. Add phases from the Projects page.
          </div>
        )}

        <div className="p360-struct-foot">
          <Link to="/projects" className="btn btn-ghost btn-sm" style={{ gap: 6, textDecoration: 'none' }}>
            Manage phases →
          </Link>
          <div style={{ fontSize: 13, color: 'var(--ns-text-muted)' }}>
            <span style={{ color: 'var(--ns-text-subtle)' }}>Total estimate</span>
            <strong>{totalBudget > 0 ? `${fmt(totalBudget)}h` : '—'}</strong>
            <span style={{ margin: '0 12px', color: 'var(--ns-text-subtle)' }}>·</span>
            <span style={{ color: 'var(--ns-text-subtle)' }}>Logged</span>
            <strong>{fmt(totalLogged)}h</strong>
          </div>
        </div>
      </div>
    </section>
  )
}

function phaseSchedulePct(phase: Phase): number | undefined {
  if (!phase.startDate || !phase.endDate) return undefined
  const s = new Date(phase.startDate + 'T00:00:00').getTime()
  const e = new Date(phase.endDate   + 'T00:00:00').getTime()
  const now = Date.now()
  if (e <= s) return undefined
  return Math.max(0, Math.min(100, Math.round(((now - s) / (e - s)) * 100)))
}

// ─── Team table ──────────────────────────────────────────────────────────────

interface TeamRow {
  personId:   number
  personName: string
  email:      string | null
  budgetHrs:  number
  loggedHrs:  number  // approximated below (we don't have per-person logged-hours endpoint)
}

function TeamTable({ rows }: { rows: TeamRow[] }) {
  if (rows.length === 0) {
    return (
      <div className="card">
        <div className="p360-card-head">
          <div className="p360-card-title">Team</div>
        </div>
        <div className="p360-card-body muted" style={{ fontSize: 12.5 }}>
          No resources assigned yet.
        </div>
      </div>
    )
  }

  return (
    <div className="card">
      <div className="p360-card-head">
        <div>
          <div className="p360-card-title">Team · {rows.length} resources</div>
          <div className="p360-card-sub">Budget allocation across phases</div>
        </div>
      </div>
      <table className="p360-team-table">
        <thead>
          <tr>
            <th>Resource</th>
            <th className="r">Budget</th>
            <th className="r">Share</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(r => {
            const totalBudget = rows.reduce((a, b) => a + b.budgetHrs, 0)
            const sharePct = pctOf(r.budgetHrs, totalBudget)
            const tone: 'warn' | 'danger' | '' = sharePct >= 80 ? 'danger' : sharePct >= 50 ? 'warn' : ''
            return (
              <tr key={r.personId}>
                <td>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <div className="p360-avatar sm" style={avatarStyle(r.personName)}>{initials(r.personName)}</div>
                    <div>
                      <div style={{ fontWeight: 500 }}>{r.personName}</div>
                      {r.email && <div style={{ fontSize: 11.5, color: 'var(--ns-text-subtle)' }}>{r.email}</div>}
                    </div>
                  </div>
                </td>
                <td className="r">{r.budgetHrs > 0 ? `${fmt(r.budgetHrs)}h` : '—'}</td>
                <td className="r">
                  <div className="p360-util">
                    <div className={`p360-util-bar ${tone}`}>
                      <div style={{ width: `${sharePct}%` }} />
                    </div>
                    <span style={{ fontSize: 12, minWidth: 30 }}>{sharePct}%</span>
                  </div>
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

// ─── Page ─────────────────────────────────────────────────────────────────────

interface ProjectOverviewPageProps {
  projectId: number
}

export default function ProjectOverviewPage({ projectId }: ProjectOverviewPageProps) {
  const queryClient = useQueryClient()
  const [editOpen, setEditOpen] = useState(false)

  const { data: project, isLoading, isError } = useQuery({
    queryKey: ['projects', projectId, 'detail'],
    queryFn:  () => api.get<Project>(`/projects/${projectId}`),
  })

  // Fetched only when the user opens the edit drawer — the drawer needs the
  // full customer list for its customer picker.
  const { data: customers = [] } = useQuery({
    queryKey: ['fortnox', 'customers'],
    queryFn:  () => api.get<FortnoxCustomer[]>('/fortnox/customers'),
    enabled:  editOpen,
  })

  const refreshProject = () => {
    queryClient.invalidateQueries({ queryKey: ['projects'] })
    queryClient.invalidateQueries({ queryKey: ['projects', projectId, 'detail'] })
    queryClient.invalidateQueries({ queryKey: ['projects', projectId, 'hours-summary'] })
    queryClient.invalidateQueries({ queryKey: ['projects', projectId, 'phases'] })
  }

  const { data: hours } = useQuery({
    queryKey: ['projects', projectId, 'hours-summary'],
    queryFn:  () => api.get<ProjectHoursSummary>(`/projects/${projectId}/hours-summary`),
  })

  const { data: phases = [] } = useQuery({
    queryKey: ['projects', projectId, 'phases'],
    queryFn:  () => api.get<Phase[]>(`/projects/${projectId}/phases`),
  })

  // Fan out per-phase resources + activities in parallel.
  const phaseQueries = useQueries({
    queries: phases.flatMap(p => [
      {
        queryKey: ['phases', p.id, 'resources'],
        queryFn:  () => api.get<PhaseResource[]>(`/phases/${p.id}/resources`),
      },
      {
        queryKey: ['phases', p.id, 'activities'],
        queryFn:  () => api.get<Activity[]>(`/phases/${p.id}/activities`),
      },
    ]),
  })

  const phaseDetails: PhaseDetail[] = useMemo(() => {
    const byName = new Map<string, number>()
    for (const row of hours?.byPhase ?? []) {
      if (row.phaseName != null) byName.set(row.phaseName, row.workedHrs)
    }
    return phases
      .filter(p => p.isActive)
      .sort((a, b) => a.priority - b.priority)
      .map((phase, i) => {
        const resources  = (phaseQueries[i * 2]?.data     as PhaseResource[] | undefined) ?? []
        const activities = (phaseQueries[i * 2 + 1]?.data as Activity[]      | undefined) ?? []
        const budgetHrs  = resources.reduce((s, r) => s + (r.budgetHours ?? 0), 0)
        const loggedHrs  = byName.get(phase.name) ?? 0
        return { phase, activities, resources, budgetHrs, loggedHrs }
      })
  }, [phases, phaseQueries, hours])

  const totalBudget = phaseDetails.reduce((s, p) => s + p.budgetHrs, 0)
  const totalLogged = hours?.totalWorkedHrs ?? 0

  const startDate = useMemo(() => {
    const ds = phaseDetails.map(p => p.phase.startDate).filter(Boolean) as string[]
    return ds.length ? ds.sort()[0] : null
  }, [phaseDetails])
  const endDate = useMemo(() => {
    const ds = phaseDetails.map(p => p.phase.endDate).filter(Boolean) as string[]
    return ds.length ? ds.sort()[ds.length - 1] : null
  }, [phaseDetails])

  const teamRows: TeamRow[] = useMemo(() => {
    const byPerson = new Map<number, TeamRow>()
    for (const { resources } of phaseDetails) {
      for (const r of resources) {
        const existing = byPerson.get(r.personId)
        if (existing) {
          existing.budgetHrs += r.budgetHours ?? 0
        } else {
          byPerson.set(r.personId, {
            personId:   r.personId,
            personName: r.personName,
            email:      r.personEmail,
            budgetHrs:  r.budgetHours ?? 0,
            loggedHrs:  0,
          })
        }
      }
    }
    return Array.from(byPerson.values()).sort((a, b) => b.budgetHrs - a.budgetHrs)
  }, [phaseDetails])

  if (isLoading) {
    return <div className="page"><div className="muted" style={{ padding: 32, textAlign: 'center', fontSize: 13 }}>Loading…</div></div>
  }
  if (isError || !project) {
    return <div className="page"><div className="muted" style={{ padding: 32, textAlign: 'center', fontSize: 13 }}>Project not found.</div></div>
  }

  const activeCount  = phaseDetails.length
  const closedCount  = phases.filter(p => p.isActive && p.status === 'closed').length
  const teamCount    = teamRows.length

  return (
    <div className="page">
      <Link
        to="/projects"
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 6,
          fontSize: 12, color: 'var(--ns-text-muted)',
          textDecoration: 'none', marginBottom: 8,
        }}
      >
        <ArrowLeft size={13} />All projects
      </Link>

      {/* Hero */}
      <div className="p360-hero">
        <div className="p360-hero-left">
          <div className="p360-icon">{initials(project.name)}</div>
          <div className="p360-title">
            <div className="p360-crumbs">
              <Link to="/projects">All projects</Link>
              <span>›</span>
              {project.customerId != null && project.customerName ? (
                <Link to="/customers/$id" params={{ id: String(project.customerId) }}>{project.customerName}</Link>
              ) : (
                <span>{project.customerName ?? 'Internal'}</span>
              )}
            </div>
            <h1>
              {project.name}
              <span className={`p360-status ${project.status}`}>{project.status.replace('_', ' ')}</span>
            </h1>
            <div className="p360-hero-meta">
              {(startDate || endDate) && (
                <div>
                  <Calendar size={13} />
                  <strong>{fmtDate(startDate)} → {fmtDate(endDate)}</strong>
                </div>
              )}
              {project.projectManagerName && (
                <div>
                  <UserIcon size={13} />
                  PM <strong>{project.projectManagerName}</strong>
                </div>
              )}
              <div>
                <Users size={13} />
                {teamCount} {teamCount === 1 ? 'resource' : 'resources'} · {fmt(totalLogged)}{totalBudget > 0 ? ` / ${fmt(totalBudget)}` : ''}h
              </div>
              {project.fortnoxProjectNumber && (
                <div>
                  <ExternalLink size={13} />
                  <span className="mono"><strong>#{project.fortnoxProjectNumber}</strong></span>
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="p360-hero-actions">
          <button className="btn" style={{ gap: 6 }} onClick={() => setEditOpen(true)}>
            <Pencil size={13} />Edit
          </button>
          <Link to="/time" className="btn btn-brand" style={{ gap: 6, textDecoration: 'none' }}>
            <Clock size={13} />Log time
          </Link>
        </div>
      </div>

      {/* Tabs (Overview only for now) */}
      <div className="p360-tabs">
        <button className="on">Overview</button>
        <button disabled>Structure<span className="count">{activeCount}</span></button>
        <button disabled>Gantt</button>
        <button disabled>Time<span className="count">{hours?.entryCount ?? 0}</span></button>
        <button disabled>Resources<span className="count">{teamCount}</span></button>
        <button disabled>Invoices</button>
        <button disabled>Settings</button>
      </div>

      <Kpis
        hours={hours}
        phases={phases}
        totalBudget={totalBudget}
        startDate={startDate}
        endDate={endDate}
      />

      <section className="p360-grid-2-1">
        <PropertiesCard
          project={project}
          startDate={startDate}
          endDate={endDate}
          totalBudget={totalBudget}
          onEdit={() => setEditOpen(true)}
        />
        <ManagerCard project={project} />
      </section>

      <StructureCard
        phaseDetails={phaseDetails}
        totalBudget={totalBudget}
        totalLogged={totalLogged}
      />

      <section className="p360-grid-2-1" style={{ marginBottom: 16 }}>
        <TeamTable rows={teamRows} />

        <div className="card">
          <div className="p360-card-head">
            <div>
              <div className="p360-card-title">Hours by phase</div>
              <div className="p360-card-sub">Logged time split</div>
            </div>
          </div>
          <div className="p360-card-body">
            {(hours?.byPhase?.length ?? 0) === 0 ? (
              <div className="muted" style={{ fontSize: 12.5 }}>No time logged yet.</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {hours?.byPhase.map((row, i) => {
                  const pct = pctOf(row.workedHrs, totalLogged)
                  return (
                    <div key={i}>
                      <div style={{
                        display: 'flex', justifyContent: 'space-between',
                        fontSize: 12.5, marginBottom: 4,
                      }}>
                        <span style={{ fontWeight: 500 }}>
                          {row.phaseName ?? <em className="muted">No phase</em>}
                        </span>
                        <span className="mono tabular" style={{ color: 'var(--ns-text-muted)' }}>
                          {fmt(row.workedHrs)}h ({pct}%)
                        </span>
                      </div>
                      <div className="p360-pbar">
                        <div className="fill" style={{ width: `${pct}%` }} />
                      </div>
                    </div>
                  )
                })}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Rates — keeps the existing functional surface from the old page */}
      <section style={{ marginBottom: 24 }}>
        <h2 style={{ fontSize: 12, fontWeight: 600, marginBottom: 10, color: 'var(--ns-text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
          <Folder size={11} style={{ display: 'inline', marginRight: 6, verticalAlign: 'middle' }} />
          Project rate
        </h2>
        <p className="muted" style={{ fontSize: 12, marginTop: 0, marginBottom: 8 }}>
          Overrides the workspace default. Per-phase or per-activity rates win when set.
        </p>
        <RatesTable scope="project" parentId={projectId} />
      </section>

      {editOpen && (
        <ProjectDrawer
          project={project}
          customers={customers}
          onClose={() => setEditOpen(false)}
          onSaved={() => {
            refreshProject()
            setEditOpen(false)
          }}
        />
      )}
    </div>
  )
}

// Silence unused-import noise from the section-level icons we expose
// in copy but don't always render (eg when there's no description).
void Layers
